import React, { useState, useEffect, useRef } from 'react';
import {
  Pane,
  Text,
  Button,
  IconButton,
  Badge,
  Spinner,
  toaster,
  Dialog,
  Tooltip,
  DocumentIcon,
  EditIcon,
  TrashIcon,
  ArrowRightIcon,
  DownloadIcon
} from 'evergreen-ui';
import MDEditor from '@uiw/react-md-editor';
import CommentEditor from './CommentEditor';
import api from '../../services/api';

const CommentItem = ({ 
  comment, 
  onReply, 
  onEdit, 
  onDelete, 
  onUpdate,
  currentUserId,
  projectId,
  entityType,
  entityId,
  level = 0 
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const markdownContentRef = useRef(null);

  const isOwner = comment.created_by === currentUserId;
  const canEdit = isOwner;
  const canDelete = isOwner;
  const canReply = true; // Anyone can reply

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleReply = async (content, attachments) => {
    setIsLoading(true);
    try {
      const response = await api.post(`/projects/${projectId}/comments`, {
        content,
        parent_id: comment.id,
        [entityType]: entityId
      });

      // Handle attachments if any
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.file) {
            const formData = new FormData();
            formData.append('file', attachment.file);
            if (attachment.description) {
              formData.append('description', attachment.description);
            }
            
            await api.post(
              `/projects/${projectId}/comments/${response.data.id}/attachments`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          }
        }
      }

      onReply(response.data);
      setIsReplying(false);
      toaster.success('Reply posted successfully');
    } catch (error) {
      toaster.danger('Failed to post reply');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (content, attachments) => {
    setIsLoading(true);
    try {
      const response = await api.put(`/projects/${projectId}/comments/${comment.id}`, {
        content
      });

      // Handle attachments if any
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.file) {
            const formData = new FormData();
            formData.append('file', attachment.file);
            if (attachment.description) {
              formData.append('description', attachment.description);
            }
            
            await api.post(
              `/projects/${projectId}/comments/${comment.id}/attachments`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          }
        }
      }

      onEdit(comment.id, response.data);
      setIsEditing(false);
      toaster.success('Comment updated successfully');
    } catch (error) {
      toaster.danger('Failed to update comment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await api.delete(`/projects/${projectId}/comments/${comment.id}`);
      onDelete(comment.id);
      setShowDeleteDialog(false);
      toaster.success('Comment deleted successfully');
    } catch (error) {
      toaster.danger('Failed to delete comment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      console.log('Downloading attachment:', attachment);
      
      if (!attachment.download_url) {
        toaster.danger('Download URL not available');
        return;
      }

      console.log('Download URL:', attachment.download_url);

      // Try multiple download methods
      let downloadSuccess = false;
      
      // Method 1: Direct download with fetch
      try {
        const response = await fetch(attachment.download_url, {
          method: 'GET',
          mode: 'cors'
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', attachment.original_filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          
          window.URL.revokeObjectURL(url);
          downloadSuccess = true;
        }
      } catch (fetchError) {
        console.log('Fetch method failed:', fetchError);
      }
      
      // Method 2: Direct link (fallback)
      if (!downloadSuccess) {
        try {
          const link = document.createElement('a');
          link.href = attachment.download_url;
          link.setAttribute('download', attachment.original_filename);
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
          document.body.appendChild(link);
          link.click();
          link.remove();
          downloadSuccess = true;
        } catch (linkError) {
          console.log('Direct link method failed:', linkError);
        }
      }
      
      // Method 3: Open in new tab
      if (!downloadSuccess) {
        try {
          window.open(attachment.download_url, '_blank');
          downloadSuccess = true;
        } catch (openError) {
          console.log('Open in new tab failed:', openError);
        }
      }
      
      if (!downloadSuccess) {
        throw new Error('All download methods failed');
      }
      
    } catch (error) {
      console.error('Download error:', error);
      toaster.danger('Failed to download attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle clicks on markdown images to show full-size preview
  useEffect(() => {
    const markdownPane = markdownContentRef.current;
    if (!markdownPane) return;

    const handleImageClick = (event) => {
      if (event.target.tagName === 'IMG') {
        event.preventDefault();
        const imgSrc = event.target.src;
        const imgAlt = event.target.alt || 'Image';

        // Create a temporary attachment-like object for the dialog
        setSelectedImage({
          download_url: imgSrc,
          thumbnail_url: imgSrc,
          original_filename: imgAlt
        });
      }
    };

    markdownPane.addEventListener('click', handleImageClick);

    return () => {
      markdownPane.removeEventListener('click', handleImageClick);
    };
  }, [comment.content]);

  return (
    <Pane
      marginLeft={level * 24}
      marginBottom={16}
      padding={16}
      backgroundColor={level === 0 ? "white" : "tint1"}
      border={level === 0 ? "1px solid #E6E8F0" : "1px solid #DDE3E8"}
      borderRadius={6}
    >
      {/* Comment Header */}
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={12}>
        <Pane display="flex" alignItems="center" gap={8}>
          <Text size={400} fontWeight="bold">
            {comment.creator?.email || 'Unknown User'}
          </Text>
          <Text size={300} color="muted">
            {formatTimeAgo(comment.created_at)}
          </Text>
          {comment.is_edited && (
            <Badge color="neutral" size="small">
              edited
            </Badge>
          )}
        </Pane>

        <Pane display="flex" alignItems="center" gap={4}>
          {canReply && (
            <Tooltip content="Reply">
              <IconButton
                icon={ArrowRightIcon}
                size="small"
                appearance="minimal"
                onClick={() => setIsReplying(!isReplying)}
              />
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip content="Edit">
              <IconButton
                icon={EditIcon}
                size="small"
                appearance="minimal"
                onClick={() => setIsEditing(!isEditing)}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip content="Delete">
              <IconButton
                icon={TrashIcon}
                size="small"
                appearance="minimal"
                intent="danger"
                onClick={() => setShowDeleteDialog(true)}
              />
            </Tooltip>
          )}
        </Pane>
      </Pane>

      {/* Comment Content */}
      {isEditing ? (
        <CommentEditor
          initialContent={comment.content}
          isEditing={true}
          isLoading={isLoading}
          onSave={handleEdit}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <Pane
          marginBottom={12}
          backgroundColor="white"
          padding={12}
          borderRadius={4}
          className="comment-markdown-content"
          ref={markdownContentRef}
        >
          <style>
            {`
              .comment-markdown-content img {
                max-width: 300px;
                max-height: 200px;
                object-fit: contain;
                cursor: pointer;
                border-radius: 4px;
                border: 1px solid #E6E8F0;
                transition: opacity 0.2s;
              }
              .comment-markdown-content img:hover {
                opacity: 0.8;
              }
            `}
          </style>
          <MDEditor.Markdown
            source={comment.content}
            data-color-mode="light"
            style={{ backgroundColor: 'white', color: 'black' }}
          />
        </Pane>
      )}

      {/* Attachments */}
      {comment.attachments && comment.attachments.length > 0 && (
        <Pane marginBottom={12}>
          <Text size={400} fontWeight="bold" marginBottom={8}>
            Attachments ({comment.attachments.length})
          </Text>
          <Pane display="flex" flexWrap="wrap" gap={8}>
            {comment.attachments.map((attachment) => (
              <Pane
                key={attachment.id}
                display="flex"
                flexDirection="column"
                padding={8}
                backgroundColor="tint1"
                borderRadius={4}
                border="1px solid #E6E8F0"
                minWidth={200}
                maxWidth={300}
              >
                {/* Image Preview - only show thumbnail if available */}
                {attachment.mime_type && attachment.mime_type.startsWith('image/') && attachment.thumbnail_url && (
                  <Pane marginBottom={8} textAlign="center">
                    <img
                      src={attachment.thumbnail_url}
                      alt={attachment.original_filename}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '150px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        objectFit: 'contain'
                      }}
                      onClick={() => setSelectedImage(attachment)}
                    />
                  </Pane>
                )}
                
                {/* File Info */}
                <Pane display="flex" alignItems="center">
                  <DocumentIcon size={16} marginRight={8} />
                  <Pane flex={1}>
                    <Text size={300} fontWeight="bold" truncate>
                      {attachment.original_filename}
                    </Text>
                    <Text size={300} color="muted">
                      {formatFileSize(attachment.file_size)}
                    </Text>
                  </Pane>
                  <Tooltip content="Download">
                    <IconButton
                      icon={DownloadIcon}
                      size="small"
                      appearance="minimal"
                      onClick={() => handleDownloadAttachment(attachment)}
                    />
                  </Tooltip>
                </Pane>
              </Pane>
            ))}
          </Pane>
        </Pane>
      )}

      {/* Reply Editor */}
      {isReplying && (
        <Pane marginTop={16} padding={16} backgroundColor="tint1" borderRadius={6}>
          <Text size={400} fontWeight="bold" marginBottom={12}>
            Reply to {comment.creator?.email || 'Unknown User'}
          </Text>
          <CommentEditor
            isLoading={isLoading}
            onSave={handleReply}
            onCancel={() => setIsReplying(false)}
          />
        </Pane>
      )}

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <Pane marginTop={16}>
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onUpdate={onUpdate}
              currentUserId={currentUserId}
              projectId={projectId}
              entityType={entityType}
              entityId={entityId}
              level={level + 1}
            />
          ))}
        </Pane>
      )}

      {/* Image Preview Dialog */}
      {selectedImage && (
        <Dialog
          isShown={!!selectedImage}
          title="Image Preview"
          onCloseComplete={() => setSelectedImage(null)}
          hasFooter={false}
          width="90vw"
          maxWidth="1200px"
        >
          <Pane padding={16}>
            <img
              src={selectedImage.download_url || selectedImage.thumbnail_url}
              alt={selectedImage.original_filename}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          </Pane>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        isShown={showDeleteDialog}
        title="Delete Comment"
        intent="danger"
        onCloseComplete={() => setShowDeleteDialog(false)}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        isConfirmLoading={isLoading}
      >
        Are you sure you want to delete this comment? This action cannot be undone.
        {comment.reply_count > 0 && (
          <Text marginTop={8} color="orange">
            This will also delete {comment.reply_count} reply(ies).
          </Text>
        )}
      </Dialog>
    </Pane>
  );
};

export default CommentItem;
